import {computed, createApp, shallowReadonly} from 'vue'
import {createPinia} from 'pinia'
//import * as Sentry from "@sentry/vue";
import DesignSystem from '@hantera/design-system'
import '@hantera/design-system/styles.css'
import '@hantera/portal-app/dist/style.css'

import Root from './Root.vue'
import initialize from './initialize'
import {
  defaultViewUriKey,
  mittKey,
  phraseContextKey,
  stateKey,
  viewRepositoryKey,
  webSocketSubscribeKey,
  webSocketUnsubscribeKey
} from './keys'
import mitt from 'mitt'
import {
  backendApiKey,
  contextKey,
  hostKey,
  legacyModeKey,
  PortalContext,
  Processes,
  processesKey,
  ViewContext,
  ViewOptions
} from '@hantera/portal-app'
import PrivateBackendApi from '@/backend';
import localization from './localization';
import PhrasesView from './views/Settings/PhrasesView.vue';
import LocalesView from './views/Settings/LocalesView.vue';
import CurrenciesView from './views/Settings/CurrenciesView.vue';
import AuditView from './views/Settings/AuditView.vue';
import AppsView from './views/System/AppsView';
import InventoriesView from './views/Settings/Inventories/InventoriesView.vue';
import ChannelsView from './views/Settings/Channels/ChannelsView.vue';

import PatView from './views/Pat';

import phraseLookupFactory from './phrase-lookup-factory'
import PromotionPeriods from "@/views/PromotionPeriods";

import ToastPanel from '@/ToastPanel'
import * as viewRepository from '@/view-repository'
import UserView from "@/views/User/UserView.vue";
import LegacyOrder from '@/views/LegacyOrder'
import OrderView from '@/views/OrderView.vue'
import QueryView from '@/views/QueryView'
import OrdersView from "@/views/Sales/OrdersView.vue";
import DeliveriesView from "@/views/Sales/DeliveriesView.vue";
import InvoicesView from "@/views/Sales/InvoicesView.vue";
import InventoryView from "@/views/Sales/Inventory/InventoryView.vue";
import InventorySku from "@/views/Sales/Inventory/InventorySku.vue";

// IAM views
import PrincipalsView from "@/views/Iam/Principals/PrincipalsView.vue";
import ClientsView from "@/views/Iam/Clients/ClientsView.vue";
import RolesView from "@/views/Iam/Roles/RolesView.vue";

import EmbeddedView from "@/components/Global/EmbeddedView.vue";
import FilesImage from "@/components/Global/FilesImage.vue";
import ProductSelector from "@/components/Global/ProductSelector";
import OrderLineImage from "@/components/Global/OrderLineImage.vue";
import LocalizedLabelInput from "@/components/Global/LocalizedLabelInput.vue";
import CurrencyInput from "@/components/Global/CurrencyInput.vue";
import AppSlotView from "@/AppSlotView.vue";
import JobsDashboardView from "@/views/Jobs/JobsDashboardView.vue";
import JobDefinitionDetailsView from "@/views/Jobs/JobDefinitionDetailsView.vue";
import MonitoringView from "@/views/Monitoring/MonitoringView.vue";

import {createRegistryStore} from '@/stores/registry-store';

const toastPanel = createApp(ToastPanel).mount('#toast-panel') as InstanceType<typeof ToastPanel>;

function showError(spec: {
  code?: string
  title?: string
  message: string
}) {
  toastPanel.toastError(spec);
}

const processes = {
  createProcess(spec: {
    title?: string
    description: string
  }) {

    // Processes are not tracked in fallback implementation. We only show errors

    return {
      complete(description?: string) {
        // Do nothing
      },
      fail(spec: {
        code?: string
        title?: string
        message: string
      }) {
        showError(spec)
      }
    }
  },
  error(spec: {
    code?: string
    title?: string
    message: string
  }) {
    showError(spec)
  }
} as Processes

initialize(processes)
  .then(async (result) => {
    const app = createApp(Root);
    app.provide(processesKey, processes)

    const backendApi = new PrivateBackendApi(
      result.portalFetch,
      result.host,
      processes,
      result.serverUrl,
      result.webSocketSubscribe
    );
    
    await backendApi.user.refresh();
    app.provide(backendApiKey, backendApi);
    
    const context: PortalContext = {
      serverUrl: () => result.serverUrl,
      instanceName: () => result.instanceName,
      user: () => backendApi.user.value,
      authorize: (pattern: string) => backendApi.user.authorize(pattern),
      authorizeAll: (patterns?: string[]) => backendApi.user.authorizeAll(patterns),
      mode: result.mode,
      defaultViewUri: result.defaultViewUri,
      openView(uri: string, options: ViewOptions): ViewContext {
        // open is replaced by view managers
        throw new Error('PortalContext.open not implemented - Ensure view manager is loaded')
      },
      getView(viewId: string): ViewContext | undefined {
        // getView is replaced by view managers
        throw new Error('PortalContext.getView not implemented - Ensure view manager is loaded')
      },
      getChildrenViews(parentViewId?: string): ViewContext[] {
        // getChildrenViews is replaced by view managers
        throw new Error('PortalContext.getChildrenViews not implemented - Ensure view manager is loaded')
      },
      currentView: computed<ViewContext | undefined>((): ViewContext | undefined => {
        return undefined
      }),
    };
    app.provide(contextKey, shallowReadonly(context));

    const phraseLookup = phraseLookupFactory(backendApi);
    
    app.provide(webSocketSubscribeKey, result.webSocketSubscribe);
    app.provide(webSocketUnsubscribeKey, result.webSocketUnsubscribe);
    
    app.provide('$portal_eventsService', result.eventsService);
    app.provide('$portal_liveQueryService', result.liveQueryService);
    
    app.provide('$portal_phraseLookup', phraseLookup);
    app.provide('$portal_localization', localization);
    app.provide(phraseContextKey, '');
    
    if (result.defaultViewUri) {
      app.provide(defaultViewUriKey, result.defaultViewUri)
    }
    if (result.stateKey) {
      app.provide(stateKey, result.stateKey)
    }

    app.provide(hostKey, result.host);
    app.provide(mittKey, mitt());
    app.provide(legacyModeKey, result.legacyMode);
    
    app.provide(viewRepositoryKey, viewRepository)
    
    const pinia = createPinia()
    app.use(pinia)
    app.use(DesignSystem);

    const registryStore = createRegistryStore(backendApi, processes)
    app.provide('$portal_registryStore', registryStore)

    viewRepository.registerView('/user', UserView, ['me:read', 'me:write'])
    viewRepository.registerView('/query', QueryView)
    viewRepository.registerView('/order', OrderView, ['graph:read'])
    viewRepository.registerView('/sales/inventory/sku', InventorySku, ['graph:read'])
    viewRepository.registerView('/legacy-order', LegacyOrder, ['graph:read'])
    viewRepository.registerView('/pat', PatView)
    viewRepository.registerView('/settings/phrases', PhrasesView)
    viewRepository.registerView('/settings/locales', LocalesView, ['registry/locales:read', 'registry/locales:write'])
    viewRepository.registerView('/settings/currencies', CurrenciesView, ['registry/currencies:read', 'registry/currencies:write'])
    viewRepository.registerView('/settings/inventories', InventoriesView, ['registry/inventories:read', 'registry/inventories:write'])
    viewRepository.registerView('/settings/channels', ChannelsView, ['registry/inventories:read', 'registry/channels:write'])
    viewRepository.registerView('/settings/audit', AuditView)
    viewRepository.registerView('/system/apps', AppsView)
    viewRepository.registerView('/marketing/promotion-periods', PromotionPeriods)
    
    viewRepository.registerView('/sales/orders', OrdersView)
    viewRepository.registerView('/sales/deliveries', DeliveriesView)
    viewRepository.registerView('/sales/invoices', InvoicesView)
    viewRepository.registerView('/sales/inventory', InventoryView, ['graph:read'])
    
    viewRepository.registerView('/jobs/dashboard', JobsDashboardView)
    viewRepository.registerView('/jobs/definition', JobDefinitionDetailsView)

    // Registered without an access pattern: each tab gates itself on the permission its own
    // endpoints check, and any one of them is enough to make the view worth opening. A single
    // pattern here is what made the view this replaces unopenable for the roles it was built for.
    viewRepository.registerView('/system/monitoring', MonitoringView)


    // IAM views
    viewRepository.registerView('/iam/principals', PrincipalsView, ['iam/principals:read'])
    viewRepository.registerView('/iam/clients', ClientsView, ['iam/clients:read'])
    viewRepository.registerView('/iam/roles', RolesView, ['iam/roles:read'])


    // if (process.env.NODE_ENV === 'production') {
    //   Sentry.init({
    //     app,
    //     dsn: "https://34d3f02a3bddf0c331ace59f5a551a0d@o4508665227509760.ingest.de.sentry.io/4508733192077392",
    //     integrations: [
    //       Sentry.replayIntegration(),
    //     ],
    //     // Performance Monitoring
    //     tracesSampleRate: 0,
    //     tracePropagationTargets: ["localhost", /https:\/\/.+?\.portal\..+?\.hantera.cloud.+/],
    //     replaysSessionSampleRate: 0,
    //     replaysOnErrorSampleRate: 1.0,
    //   });
    //   Sentry.setUser({
    //     id: backendApi.user.value.email
    //   });
    //   Sentry.setTag("notification", false);
    // }

    app.component('AppSlotView', AppSlotView)
    
    // Global components
    app.component('EmbeddedView', EmbeddedView)
    app.component('FilesImage', FilesImage)
    app.component('ProductSelector', ProductSelector)
    app.component('OrderLineImage', OrderLineImage)
    app.component('LocalizedLabelInput', LocalizedLabelInput)
    app.component('CurrencyInput', CurrencyInput)
    
    app.mount('#app');

  });
